import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/** The one way a status is shown everywhere: a dot, a short label, and a tone. */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand'

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-ink/[0.06] text-ink-soft',
  success: 'bg-success/[0.12] text-success',
  warning: 'bg-warning/[0.14] text-warning',
  danger: 'bg-danger/[0.12] text-danger',
  info: 'bg-info/[0.12] text-info',
  brand: 'bg-brand-soft text-brand',
}

export function StatusPill({
  tone = 'neutral',
  children,
  className,
  size = 'md',
  title,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
  size?: 'sm' | 'md'
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill font-medium',
        size === 'sm' ? 'h-5 px-2 text-[11px]' : 'h-6 px-2.5 text-[12px]',
        TONE_CLASS[tone],
        className,
      )}
    >
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  )
}

export const COUPON_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  active: { label: 'Active', tone: 'success' },
  inactive: { label: 'Inactive', tone: 'warning' },
}

export const COMMISSION_STATUS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: 'To be settled', tone: 'info' },
  settled: { label: 'Settled', tone: 'success' },
  void: { label: 'Void', tone: 'neutral' },
}

export const REFERRAL_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  past_due: { label: 'Past due', tone: 'warning' },
  unknown: { label: 'Unknown', tone: 'neutral' },
}

export function Pill({ map, value, size }: { map: Record<string, { label: string; tone: Tone }>; value: string; size?: 'sm' | 'md' }) {
  const meta = map[value] ?? { label: value, tone: 'neutral' as Tone }
  return (
    <StatusPill tone={meta.tone} size={size}>
      {meta.label}
    </StatusPill>
  )
}
